import React, { useMemo, useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Checkbox,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Alert,
  Chip,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SuperAppButton from "../SuperAppButton";

// ---------- OPTIONAL (Dev-only): Silence Chrome ResizeObserver warning ----------
const useSilenceResizeObserverWarning = () => {
  useEffect(() => {
    const handler = (e) => {
      if (
        e?.message ===
        "ResizeObserver loop completed with undelivered notifications."
      ) {
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener("error", handler);
    return () => window.removeEventListener("error", handler);
  }, []);
};
// -----------------------------------------------------------------------------

/** Example seed data (you can pass your Viator JSON via props) */
const apiDatas = [
  {
    baseUrl: "https://api.sandbox.viator.com/partner",
    headers: [
      {
        VALUE: "en, en-AU",
        description:
          "⭐ Specifies the language into which the natural-language fields in the response from this service will be translated. Example: en, en-AU",
        isApiKey: false,
        isDefault: false,
        name: "Accept-Language",
        required: true,
        type: "string",
      },
      {
        VALUE: "asdfsad",
        description: "⭐ API key for the call.",
        isApiKey: true,
        isDefault: true,
        name: "exp-api-key",
        required: true,
        type: "string",
      },
      {
        VALUE: "application/json;version=2.0",
        description: "⭐Header needed for the API call",
        isApiKey: false,
        isDefault: true,
        name: "accept",
        required: true,
        type: "string",
      },
    ],
    method: "post",
    params: [
      {
        VALUE: "string",
        description: "⚪ The campaign identifier. Example: 12345",
        isApiKey: false,
        isDefault: false,
        name: "campaign",
        required: false,
        type: "string",
      },
      {
        VALUE: "string",
        description:
          "⚪ Target lander page for affiliate productUrl. Example: target lander",
        isApiKey: false,
        isDefault: false,
        name: "target-lander",
        required: false,
        type: "string",
        // Example of a URL piece you want outside: uncomment to see it appear
        // withUrl: true
      },
    ],
    path: "/search/freetext",
    payload: [
      {
        VALUE: "big",
        description: "⭐ Free-text search term. Example: big",
        isApiKey: false,
        isDefault: false,
        name: "searchTerm",
        required: true,
        type: "string",
      },
      {
        VALUE: {
          data: [
            {
              VALUE: "77",
              description: "⚪ Filter by destination ID. Example: 77",
              isApiKey: false,
              isDefault: false,
              name: "destination",
              required: false,
              type: "string",
            },
            {
              VALUE: {
                data: [
                  {
                    VALUE: "2023-01-01",
                    description:
                      "⚪ Start date for the date range (YYYY-MM-DD). Example: 2023-01-01",
                    isApiKey: false,
                    isDefault: false,
                    name: "from",
                    required: false,
                    type: "string",
                  },
                  {
                    VALUE: "2023-01-31",
                    description:
                      "⚪ End date for the date range (YYYY-MM-DD). Example: 2023-01-31",
                    isApiKey: false,
                    isDefault: false,
                    name: "to",
                    required: false,
                    type: "string",
                  },
                ],
                type: "object",
              },
              description: "⚪ Date range for product availability.",
              isApiKey: false,
              isDefault: false,
              name: "dateRange",
              required: false,
              type: "object",
            },
          ],
          type: "object",
        },
        description: "⚪ Filters to be applied to product results.",
        isApiKey: false,
        isDefault: false,
        name: "productFiltering",
        required: false,
        type: "object",
      },
      {
        VALUE: {
          data: [
            {
              VALUE: "PRICE",
              description:
                "⚪ Sort products by PRICE. Allowed values: PRICE. Example: PRICE",
              isApiKey: false,
              isDefault: false,
              name: "sort",
              required: false,
              type: "string",
            },
            {
              VALUE: "DESCENDING",
              description:
                "⚪ Sort order. Allowed values: DESCENDING. Example: DESCENDING",
              isApiKey: false,
              isDefault: false,
              name: "order",
              required: false,
              type: "string",
            },
          ],
          type: "object",
        },
        description: "⚪ Sorting options for product results.",
        isApiKey: false,
        isDefault: false,
        name: "productSorting",
        required: false,
        type: "object",
      },
    ],
  },
];

const QUICK_TARGETS = [
  { value: "headers", label: "Headers" },
  { value: "params", label: "Params" },
  { value: "withUrl", label: "With URL" }, // stored in params with {withUrl:true}
  { value: "payload", label: "Payload" },
];

export default function ApiSelector({ apiData = apiDatas, onSubmit }) {
  useSilenceResizeObserverWarning();

  // Deep clone once to freely edit without mutating props
  const initial = useMemo(() => JSON.parse(JSON.stringify(apiData)), [apiData]);
  const [apis, setApis] = useState(initial);
  const [selectedApis, setSelectedApis] = useState([]);
  const [expanded, setExpanded] = useState(null); // top-level API accordion control

  // per-API quick-add form state
  const [quickOpen, setQuickOpen] = useState({}); // { [apiIndex]: boolean }
  const [quickForm, setQuickForm] = useState({}); // { [apiIndex]: { name, value, target } }

  // per-field inline editor state for API-keys and other fields in sections
  // key: `${apiIndex}:${section}:${fieldIndex}` -> {open:boolean, value:string}
  const [editMap, setEditMap] = useState({});

  // meta editors for baseUrl / path
  // key: `${apiIndex}:meta:${metaKey}` -> {open:boolean, value:string}
  const [metaEditMap, setMetaEditMap] = useState({});

  const keyFor = (apiIndex, section, fieldIndex) =>
    `${apiIndex}:${section}:${fieldIndex}`;
  const metaKeyFor = (apiIndex, metaKey) => `${apiIndex}:meta:${metaKey}`;

  // ---- VALUE NORMALIZATION (critical for nested Viator JSON) ----
  const normalizeDisplay = (v) => {
    if (v === undefined || v === null) return "";
    if (typeof v === "object" && v && "data" in v && "type" in v) {
      try {
        return JSON.stringify(v.data, null, 2);
      } catch {
        return String(v);
      }
    }
    const t = typeof v;
    if (t === "string" || t === "number" || t === "boolean") return String(v);
    try {
      return JSON.stringify(v, null, 2);
    } catch {
      return String(v);
    }
  };

  // Ensure the editor always holds a string
  const valueToEditorString = (v) => normalizeDisplay(v);

  const openEditor = (apiIndex, section, fieldIndex, currentValue) => {
    setEditMap((m) => ({
      ...m,
      [keyFor(apiIndex, section, fieldIndex)]: {
        open: true,
        value: valueToEditorString(currentValue) || "",
      },
    }));
  };

  const closeEditor = (apiIndex, section, fieldIndex) => {
    setEditMap((m) => {
      const copy = { ...m };
      delete copy[keyFor(apiIndex, section, fieldIndex)];
      return copy;
    });
  };

  const setEditorValue = (apiIndex, section, fieldIndex, newValue) => {
    const k = keyFor(apiIndex, section, fieldIndex);
    setEditMap((m) => ({
      ...m,
      [k]: { ...(m[k] || { open: true, value: "" }), value: newValue },
    }));
  };

  const saveEditor = (apiIndex, section, fieldIndex) => {
    const k = keyFor(apiIndex, section, fieldIndex);
    const entry = editMap[k];
    if (!entry) return;

    const newVal = entry.value ?? "";
    if (newVal === "") return;

    setApis((prev) => {
      const cp = [...prev];
      const api = { ...cp[apiIndex] };
      const list = Array.isArray(api[section]) ? [...api[section]] : [];
      const field = { ...(list[fieldIndex] || {}) };

      // Persist (as string for safety) & mirror to VALUE for seed compatibility
      field.value = newVal;
      field.VALUE = newVal;

      // If user fills a value for an API key, mark defaulted
      if (field.isApiKey) field.isDefault = true;

      list[fieldIndex] = field;
      api[section] = list;
      cp[apiIndex] = api;
      return cp;
    });

    closeEditor(apiIndex, section, fieldIndex);
  };

  // ---- Meta (baseUrl/path) editor handlers ----
  const openMetaEditor = (apiIndex, metaKey, currentValue) => {
    setMetaEditMap((m) => ({
      ...m,
      [metaKeyFor(apiIndex, metaKey)]: {
        open: true,
        value: valueToEditorString(currentValue) || "",
      },
    }));
  };

  const closeMetaEditor = (apiIndex, metaKey) => {
    setMetaEditMap((m) => {
      const copy = { ...m };
      delete copy[metaKeyFor(apiIndex, metaKey)];
      return copy;
    });
  };

  const setMetaEditorValue = (apiIndex, metaKey, newValue) => {
    const k = metaKeyFor(apiIndex, metaKey);
    setMetaEditMap((m) => ({
      ...m,
      [k]: { ...(m[k] || { open: true, value: "" }), value: newValue },
    }));
  };

  const saveMetaEditor = (apiIndex, metaKey) => {
    const k = metaKeyFor(apiIndex, metaKey);
    const entry = metaEditMap[k];
    if (!entry) return;
    const newVal = entry.value ?? "";
    if (newVal === "") return;

    setApis((prev) => {
      const cp = [...prev];
      const api = { ...cp[apiIndex] };
      api[metaKey] = String(newVal);
      cp[apiIndex] = api;
      return cp;
    });

    closeMetaEditor(apiIndex, metaKey);
  };

  const toggleSelected = (path) => {
    setSelectedApis((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
    );
  };

  const handleSubmit = () => {
    const filtered = apis.filter((api) => selectedApis.includes(api.path));
    onSubmit?.(filtered);
  };

  // ---- Quick Add (name/value/isDefault=true/target) ----
  const openQuickForm = (idx) => {
    setQuickOpen((prev) => ({ ...prev, [idx]: true }));
    setQuickForm((prev) => ({
      ...prev,
      [idx]: { name: "", value: "", target: "headers" },
    }));
  };

  const closeQuickForm = (idx) => {
    setQuickOpen((prev) => ({ ...prev, [idx]: false }));
  };

  const submitQuickForm = (apiIndex) => {
    const form = quickForm[apiIndex] || {};
    const { name, value, target } = form;

    if (!name?.trim() || !value?.trim()) return;

    let section = target;
    let extra = {};
    if (target === "withUrl") {
      section = "params";
      extra = { withUrl: true };
    }

    setApis((prev) => {
      const copy = [...prev];
      const api = { ...copy[apiIndex] };
      const list = Array.isArray(api[section]) ? [...api[section]] : [];

      // Upsert by name (case-insensitive)
      const idx = list.findIndex(
        (f) =>
          String(f?.name || "").toLowerCase() === name.trim().toLowerCase()
      );

      const entry = {
        name: name.trim(),
        value: value.trim(),
        VALUE: value.trim(), // seed compatibility
        isDefault: true,
        description: "",
        type: "string",
        required: false,
        ...extra,
      };

      if (idx >= 0) {
        list[idx] = { ...list[idx], ...entry };
      } else {
        list.push(entry);
      }

      api[section] = list;
      copy[apiIndex] = api;
      return copy;
    });

    closeQuickForm(apiIndex);
  };

  // ---- READ-ONLY Section renderer (with API-key inline editor) ----
  const SectionReadOnlyAccordion = ({ apiIndex, section, title }) => {
    const list = apis[apiIndex][section] || [];
    const count = list.length;

    return (
      <>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ color: "#fff", fontWeight: 600 }}>{title}</Typography>
          <Chip
            size="small"
            label={count}
            sx={{
              height: 18,
              "& .MuiChip-label": { px: 0.6, fontSize: 12, lineHeight: "18px" },
              bgcolor: "rgba(255,255,255,0.12)",
              color: "#fff",
            }}
          />
        </Stack>

        {count === 0 ? (
          <Typography variant="body2" sx={{ color: "#fff", opacity: 0.8 }}>
            No {title.toLowerCase()}.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {list.map((field, idx) => {
              const raw = field?.value ?? field?.VALUE ?? "";
              const displayValue = normalizeDisplay(raw);
              const isApiKey = !!field?.isApiKey;
              const isDefault = !!field?.isDefault;
              const editorKey = keyFor(apiIndex, section, idx);
              const ed = editMap[editorKey];

              return (
                <Box
                  key={idx}
                  sx={{
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 1,
                    p: 1,
                    bgcolor: "rgba(255,255,255,0.04)",
                  }}
                >
                  <Grid
                    container
                    spacing={1}
                    alignItems="center"
                    sx={{ "--lh": 1.2, lineHeight: "var(--lh)" }}
                  >
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography
                        variant="caption"
                        sx={{ color: "#fff", opacity: 0.8, lineHeight: "var(--lh)" }}
                      >
                        Name
                      </Typography>
                      <Typography sx={{ color: "#fff", fontWeight: 600 }}>
                        {field.name ?? "-"}
                      </Typography>
                    </Grid>

                    <Grid item xs={6} sm={3} md={2}>
                      <Typography
                        variant="caption"
                        sx={{ color: "#fff", opacity: 0.8, lineHeight: "var(--lh)" }}
                      >
                        Type
                      </Typography>
                      <Typography sx={{ color: "#fff" }}>
                        {field.type || "string"}
                      </Typography>
                    </Grid>

                    <Grid item xs={6} sm={3} md={2}>
                      <Typography
                        variant="caption"
                        sx={{ color: "#fff", opacity: 0.8, lineHeight: "var(--lh)" }}
                      >
                        Required
                      </Typography>
                      <Typography sx={{ color: "#fff" }}>
                        {field.required ? "Yes" : "No"}
                      </Typography>
                    </Grid>

                    {/* Value + editor (only for API key fields inside section UI) */}
                    <Grid item xs={12} sm={6} md={5}>
                      <Typography
                        variant="caption"
                        sx={{ color: "#fff", opacity: 0.8, lineHeight: "var(--lh)" }}
                      >
                        Value
                      </Typography>

                      {!isApiKey ? (
                        <Typography sx={{ color: "#fff", whiteSpace: "pre-wrap" }}>
                          {displayValue || <span style={{ opacity: 0.6 }}>—</span>}
                        </Typography>
                      ) : ed?.open ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TextField
                            size="small"
                            fullWidth
                            value={ed.value}
                            onChange={(e) =>
                              setEditorValue(apiIndex, section, idx, e.target.value)
                            }
                            placeholder="Enter API key value"
                            InputLabelProps={{ sx: { color: "#BFD1FF" } }}
                            sx={{ ...textFieldDarkSx, maxWidth: 420 }}
                          />
                          <IconButton
                            aria-label="Save"
                            onClick={() => saveEditor(apiIndex, section, idx)}
                            sx={{ color: "#90CAF9" }}
                          >
                            <CheckIcon />
                          </IconButton>
                          <IconButton
                            aria-label="Cancel"
                            onClick={() => closeEditor(apiIndex, section, idx)}
                            sx={{ color: "#E6ECFF" }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Stack>
                      ) : (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography sx={{ color: "#fff", whiteSpace: "pre-wrap" }}>
                            {displayValue || <span style={{ opacity: 0.6 }}>—</span>}
                          </Typography>
                          {isApiKey && (
                            <>
                              <IconButton
                                aria-label={isDefault ? "Edit value" : "Add value"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditor(apiIndex, section, idx, raw);
                                }}
                                sx={{
                                  color: isDefault ? "#90CAF9" : "#66BB6A",
                                  ml: 0.5,
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                              <Typography
                                variant="caption"
                                sx={{ color: "rgba(230,236,255,0.8)" }}
                              >
                                {isDefault ? "Edit" : "Add Value"}
                              </Typography>
                            </>
                          )}
                        </Stack>
                      )}
                    </Grid>

                    {field.description && (
                      <Grid item xs={12}>
                        <Typography
                          variant="caption"
                          sx={{ color: "#fff", opacity: 0.8, lineHeight: "var(--lh)" }}
                        >
                          Description
                        </Typography>
                        <Typography sx={{ color: "#fff", whiteSpace: "pre-wrap" }}>
                          {normalizeDisplay(field.description)}
                        </Typography>
                      </Grid>
                    )}

                    {"withUrl" in field && field.withUrl && (
                      <Grid item xs={12}>
                        <Chip
                          size="small"
                          label="withUrl"
                          sx={{
                            bgcolor: "rgba(33,150,243,0.35)",
                            color: "#fff",
                            height: 18,
                            "& .MuiChip-label": {
                              px: 0.6,
                              fontSize: 12,
                              lineHeight: "18px",
                            },
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Box>
              );
            })}
          </Stack>
        )}
      </>
    );
  };

  // ---- API KEYS BAR just below each accordion (EDIT with Save/Cancel only) ----
  const ApiKeysBarBelowAccordion = ({ api, apiIndex }) => {
    const sections = ["headers", "params", "payload"];
    const apiKeyItems = [];

    sections.forEach((section) => {
      const list = api?.[section] || [];
      list.forEach((field, fieldIndex) => {
        if (field?.isApiKey) {
          const raw = field?.value ?? field?.VALUE ?? "";
          const displayValue = normalizeDisplay(raw);
          const isDefault = !!field?.isDefault;
          const editorKey = keyFor(apiIndex, section, fieldIndex);
          const ed = editMap[editorKey];
          apiKeyItems.push({
            section,
            fieldIndex,
            name: field.name,
            value: displayValue,
            raw,
            isDefault,
            ed,
          });
        }
      });
    });

    if (apiKeyItems.length === 0) return null;

    return (
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          mt: 0.75,
          mb: 1.5,
          px: 1.25,
          py: 1,
          borderRadius: 1,
          border: "1px dashed rgba(255,255,255,0.22)",
          bgcolor: "rgba(255,255,255,0.04)",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography sx={{ color: "#E6ECFF", fontWeight: 700 }}>
            API Keys
          </Typography>
          <Chip
            size="small"
            label={apiKeyItems.length}
            sx={{
              height: 18,
              "& .MuiChip-label": { px: 0.6, fontSize: 12, lineHeight: "18px" },
              bgcolor: "rgba(255,255,255,0.12)",
              color: "#fff",
            }}
          />
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" sx={{ color: "rgba(230,236,255,0.8)" }}>
            Shown outside the accordion for quick access
          </Typography>
        </Stack>

        <Stack spacing={0.75}>
          {apiKeyItems.map((it, idx) => {
            const label = `${it.name} (${it.section})`;

            return (
              <Stack
                key={`${it.section}:${it.fieldIndex}:${idx}`}
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "stretch", sm: "center" }}
                spacing={1}
                sx={{
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 1,
                  p: 0.75,
                  bgcolor: "rgba(255,255,255,0.02)",
                }}
              >
                <Typography sx={{ color: "#fff", fontWeight: 600, minWidth: 180 }}>
                  {label}
                </Typography>

                {it.ed?.open ? (
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    sx={{ flex: 1 }}
                  >
                    <TextField
                      size="small"
                      fullWidth
                      value={it.ed.value}
                      onChange={(e) =>
                        setEditorValue(apiIndex, it.section, it.fieldIndex, e.target.value)
                      }
                      placeholder="Enter API key value"
                      InputLabelProps={{ sx: { color: "#BFD1FF" } }}
                      sx={{ ...textFieldDarkSx, maxWidth: 520 }}
                    />
                    <IconButton
                      aria-label="Save"
                      onClick={() => saveEditor(apiIndex, it.section, it.fieldIndex)}
                      sx={{ color: "#90CAF9" }}
                    >
                      <CheckIcon />
                    </IconButton>
                    <IconButton
                      aria-label="Cancel"
                      onClick={() => closeEditor(apiIndex, it.section, it.fieldIndex)}
                      sx={{ color: "#E6ECFF" }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                    <Typography sx={{ color: "#fff", whiteSpace: "pre-wrap" }}>
                      {it.value || <span style={{ opacity: 0.6 }}>—</span>}
                    </Typography>
                    <IconButton
                      aria-label={it.isDefault ? "Edit value" : "Add value"}
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditor(apiIndex, it.section, it.fieldIndex, it.raw);
                      }}
                      sx={{ color: it.isDefault ? "#90CAF9" : "#66BB6A", ml: 0.5 }}
                    >
                      <EditIcon />
                    </IconButton>
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(230,236,255,0.8)" }}
                    >
                      {it.isDefault ? "Edit" : "Add Value"}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            );
          })}
        </Stack>
      </Box>
    );
  };

  // ---- MISSING URL BAR (Base URL / Path / withUrl params) ----
  const MissingUrlBarBelowAccordion = ({ api, apiIndex }) => {
    const missingItems = [];

    // Base URL
    const baseUrlMissing = !String(api.baseUrl ?? "").trim();
    // Path
    const pathMissing = !String(api.path ?? "").trim();

    if (baseUrlMissing) {
      const k = metaKeyFor(apiIndex, "baseUrl");
      missingItems.push({
        type: "meta",
        metaKey: "baseUrl",
        label: "Base URL",
        ed: metaEditMap[k],
        current: api.baseUrl ?? "",
      });
    }
    if (pathMissing) {
      const k = metaKeyFor(apiIndex, "path");
      missingItems.push({
        type: "meta",
        metaKey: "path",
        label: "Path",
        ed: metaEditMap[k],
        current: api.path ?? "",
      });
    }

    // Any params explicitly marked with withUrl:true and missing a value
    const withUrlParams = (api.params || []).map((f, i) => ({ f, i })).filter(
      ({ f }) => f?.withUrl === true
    );

    withUrlParams.forEach(({ f, i }) => {
      const raw = f?.value ?? f?.VALUE ?? "";
      const present = String(normalizeDisplay(raw)).trim().length > 0;
      if (!present) {
        const editorKey = keyFor(apiIndex, "params", i);
        missingItems.push({
          type: "param",
          section: "params",
          fieldIndex: i,
          name: f.name || "(param)",
          label: `Param: ${f.name || "(unnamed)"}`,
          ed: editMap[editorKey],
          current: raw,
        });
      }
    });

    if (missingItems.length === 0) return null;

    return (
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          mt: 0.75,
          mb: 1.5,
          px: 1.25,
          py: 1,
          borderRadius: 1,
          border: "1px dashed rgba(255,255,255,0.32)",
          bgcolor: "rgba(255,255,255,0.06)",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography sx={{ color: "#FFECB3", fontWeight: 700 }}>
            Missing URL Pieces
          </Typography>
          <Chip
            size="small"
            label={missingItems.length}
            sx={{
              height: 18,
              "& .MuiChip-label": { px: 0.6, fontSize: 12, lineHeight: "18px" },
              bgcolor: "rgba(255,255,255,0.18)",
              color: "#fff",
            }}
          />
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" sx={{ color: "rgba(230,236,255,0.9)" }}>
            Fill these to complete the request URL
          </Typography>
        </Stack>

        <Stack spacing={0.75}>
          {missingItems.map((it, idx) => {
            if (it.type === "meta") {
              const mk = it.metaKey;
              const ed = it.ed;
              return (
                <Stack
                  key={`meta:${mk}:${idx}`}
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  spacing={1}
                  sx={{
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 1,
                    p: 0.75,
                    bgcolor: "rgba(255,255,255,0.02)",
                  }}
                >
                  <Typography sx={{ color: "#fff", fontWeight: 600, minWidth: 140 }}>
                    {it.label}
                  </Typography>

                  {ed?.open ? (
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ xs: "stretch", sm: "center" }}
                      sx={{ flex: 1 }}
                    >
                      <TextField
                        size="small"
                        fullWidth
                        value={ed.value}
                        onChange={(e) =>
                          setMetaEditorValue(apiIndex, mk, e.target.value)
                        }
                        placeholder={`Enter ${it.label.toLowerCase()}`}
                        InputLabelProps={{ sx: { color: "#BFD1FF" } }}
                        sx={{ ...textFieldDarkSx, maxWidth: 520 }}
                      />
                      <IconButton
                        aria-label="Save"
                        onClick={() => saveMetaEditor(apiIndex, mk)}
                        sx={{ color: "#90CAF9" }}
                      >
                        <CheckIcon />
                      </IconButton>
                      <IconButton
                        aria-label="Cancel"
                        onClick={() => closeMetaEditor(apiIndex, mk)}
                        sx={{ color: "#E6ECFF" }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                      <Typography sx={{ color: "#fff", opacity: 0.7 }}>
                        — missing —
                      </Typography>
                      <IconButton
                        aria-label={`Edit ${mk}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          openMetaEditor(apiIndex, mk, it.current);
                        }}
                        sx={{ color: "#66BB6A", ml: 0.5 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <Typography
                        variant="caption"
                        sx={{ color: "rgba(230,236,255,0.8)" }}
                      >
                        Add Value
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              );
            }

            // withUrl param
            if (it.type === "param") {
              const ed = it.ed;
              return (
                <Stack
                  key={`param:${it.fieldIndex}:${idx}`}
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  spacing={1}
                  sx={{
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 1,
                    p: 0.75,
                    bgcolor: "rgba(255,255,255,0.02)",
                  }}
                >
                  <Typography sx={{ color: "#fff", fontWeight: 600, minWidth: 140 }}>
                    {it.label}
                  </Typography>

                  {ed?.open ? (
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ xs: "stretch", sm: "center" }}
                      sx={{ flex: 1 }}
                    >
                      <TextField
                        size="small"
                        fullWidth
                        value={ed.value}
                        onChange={(e) =>
                          setEditorValue(apiIndex, it.section, it.fieldIndex, e.target.value)
                        }
                        placeholder="Enter URL piece"
                        InputLabelProps={{ sx: { color: "#BFD1FF" } }}
                        sx={{ ...textFieldDarkSx, maxWidth: 520 }}
                      />
                      <IconButton
                        aria-label="Save"
                        onClick={() => saveEditor(apiIndex, it.section, it.fieldIndex)}
                        sx={{ color: "#90CAF9" }}
                      >
                        <CheckIcon />
                      </IconButton>
                      <IconButton
                        aria-label="Cancel"
                        onClick={() => closeEditor(apiIndex, it.section, it.fieldIndex)}
                        sx={{ color: "#E6ECFF" }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                      <Typography sx={{ color: "#fff", opacity: 0.7 }}>
                        — missing —
                      </Typography>
                      <IconButton
                        aria-label="Edit value"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditor(apiIndex, it.section, it.fieldIndex, it.current);
                        }}
                        sx={{ color: "#66BB6A", ml: 0.5 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <Typography
                        variant="caption"
                        sx={{ color: "rgba(230,236,255,0.8)" }}
                      >
                        Add Value
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              );
            }

            return null;
          })}
        </Stack>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: "#0B173C",
        minHeight: "100vh",
        p: { xs: 2, md: 3 },
        color: "#E6ECFF",
      }}
    >
      {apis.map((api, index) => {
        const isChecked = selectedApis.includes(api.path);
        const panelKey = api.path || String(index);
        const isQuickOpen = !!quickOpen[index];
        const qf = quickForm[index] || { name: "", value: "", target: "headers" };

        return (
          <React.Fragment key={panelKey}>
            <Accordion
              expanded={expanded === panelKey}
              onChange={(_, isExp) => setExpanded(isExp ? panelKey : null)}
              disableGutters
              square={false}
              sx={{
                mb: 0.75,
                borderRadius: 1,
                overflow: "hidden",
                bgcolor: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.12)",
                backdropFilter: "blur(2px)",
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#E6ECFF" }} />}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ width: "100%", pr: 2 }}
                >
                  <Checkbox
                    checked={isChecked}
                    onChange={() => toggleSelected(api.path)}
                    onClick={(e) => e.stopPropagation()}
                    sx={checkboxDarkSx}
                  />
                  <Typography sx={{ fontWeight: 700, color: "#E6ECFF" }}>
                    {(api.method || "").toString().toUpperCase()} {api.path}
                  </Typography>
                  <Box flex={1} />
                  <Typography sx={{ color: "rgba(230,236,255,0.9)", fontWeight: 700 }}>
                    Base URL: {api.baseUrl || <span style={{opacity:0.7}}>— missing —</span>}
                  </Typography>
                </Stack>
              </AccordionSummary>

              <AccordionDetails sx={{ bgcolor: "rgba(255,255,255,0.03)" }}>
                <Stack spacing={1.5}>
                  {/* Prompt + Quick Add SuperAppButton */}
                  <Alert
                    severity="info"
                    sx={{
                      bgcolor: "rgba(13,71,161,0.25)",
                      color: "#E6ECFF",
                      border: "1px solid rgba(255,255,255,0.12)",
                      py: 1,
                    }}
                  >
                    Is there anything missing like API key or tokens? You can add from here.
                  </Alert>

                  {!isQuickOpen ? (
                    <Box>
                      <SuperAppButton
                        size="small"
                        startIcon={<AddIcon />}
                        variant="contained"
                        sx={{ textTransform: "none", fontWeight: 600 }}
                        onClick={() => openQuickForm(index)}
                      >
                        Add
                      </SuperAppButton>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        border: "1px solid rgba(255,255,255,0.12)",
                        borderRadius: 1,
                        p: 1.5,
                        bgcolor: "rgba(255,255,255,0.04)",
                      }}
                    >
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Name"
                            value={qf.name}
                            onChange={(e) =>
                              setQuickForm((prev) => ({
                                ...prev,
                                [index]: { ...qf, name: e.target.value },
                              }))
                            }
                            placeholder="e.g., Authorization, api_key"
                            InputLabelProps={{ sx: { color: "#BFD1FF" } }}
                            sx={textFieldDarkSx}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Value"
                            value={qf.value}
                            onChange={(e) =>
                              setQuickForm((prev) => ({
                                ...prev,
                                [index]: { ...qf, value: e.target.value },
                              }))
                            }
                            placeholder="e.g., Bearer xxx, abc123"
                            InputLabelProps={{ sx: { color: "#BFD1FF" } }}
                            sx={textFieldDarkSx}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth size="small" sx={selectDarkSx}>
                            <InputLabel sx={{ color: "#BFD1FF" }}>Target</InputLabel>
                            <Select
                              label="Target"
                              value={qf.target}
                              onChange={(e) =>
                                setQuickForm((prev) => ({
                                  ...prev,
                                  [index]: { ...qf, target: e.target.value },
                                }))
                              }
                              MenuProps={{ PaperProps: { sx: menuPaperDarkSx } }}
                            >
                              {QUICK_TARGETS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography variant="body2" sx={{ color: "#90CAF9" }}>
                            isDefault: <strong>true</strong>
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <Stack direction="row" spacing={1}>
                            <SuperAppButton
                              variant="contained"
                              onClick={() => submitQuickForm(index)}
                              sx={{ textTransform: "none", fontWeight: 600 }}
                              disabled={!qf.name?.trim() || !qf.value?.trim()}
                            >
                              Add Field
                            </SuperAppButton>
                            <SuperAppButton
                              variant="text"
                              color="inherit"
                              onClick={() => closeQuickForm(index)}
                              sx={{ textTransform: "none" }}
                            >
                              Cancel
                            </SuperAppButton>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {/* Meta fields (Base URL / Method / Path) */}
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Base URL"
                        value={api.baseUrl}
                        onChange={(e) =>
                          setApis((prev) => {
                            const copy = [...prev];
                            copy[index].baseUrl = e.target.value;
                            return copy;
                          })
                        }
                        InputLabelProps={{ sx: { color: "#BFD1FF", fontWeight: 700 } }}
                        sx={{
                          ...textFieldDarkSx,
                          "& .MuiOutlinedInput-input": { fontWeight: 700, color: "#fff" },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth size="small" sx={selectDarkSx}>
                        <InputLabel sx={{ color: "#BFD1FF" }}>Method</InputLabel>
                        <Select
                          label="Method"
                          value={api?.method ? api.method.toUpperCase() : ""}
                          onChange={(e) =>
                            setApis((prev) => {
                              const copy = [...prev];
                              copy[index].method = String(e.target.value);
                              return copy;
                            })
                          }
                          MenuProps={{ PaperProps: { sx: menuPaperDarkSx } }}
                        >
                          {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => (
                            <MenuItem key={m} value={m}>
                              {m}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Path"
                        value={api.path}
                        onChange={(e) =>
                          setApis((prev) => {
                            const copy = [...prev];
                            copy[index].path = e.target.value;
                            return copy;
                          })
                        }
                        InputLabelProps={{ sx: { color: "#BFD1FF", fontWeight: 700 } }}
                        sx={{
                          ...textFieldDarkSx,
                          "& .MuiOutlinedInput-input": { fontWeight: 700, color: "#fff" },
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />

                  {/* READ-ONLY sections as compact accordions */}
                  <SectionReadOnlyAccordion apiIndex={index} section="headers" title="Headers" />
                  <SectionReadOnlyAccordion apiIndex={index} section="params" title="Parameters" />
                  <SectionReadOnlyAccordion apiIndex={index} section="payload" title="Payload" />
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Outside, just below the accordion */}
            <MissingUrlBarBelowAccordion api={api} apiIndex={index} />
            <ApiKeysBarBelowAccordion api={api} apiIndex={index} />
          </React.Fragment>
        );
      })}

      <Box sx={{ mt: 2 }}>
        <SuperAppButton
          variant="contained"
          size="medium"
          onClick={handleSubmit}
          disabled={selectedApis.length === 0}
          sx={{
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Submit Selection
        </SuperAppButton>
      </Box>
    </Box>
  );
}

/* ----------------- Dark UI helpers (so MUI inputs look good on #0B173C) ----------------- */
const baseFieldDark = {
  "& .MuiOutlinedInput-root": {
    color: "#E6ECFF",
    "& fieldset": { borderColor: "rgba(230,236,255,0.4)" },
    "&:hover fieldset": { borderColor: "#E6ECFF" },
    "&.Mui-focused fieldset": { borderColor: "#E6ECFF" },
    "& input, & textarea": {
      color: "#E6ECFF",
    },
  },
  "& .MuiFormLabel-root": { color: "#BFD1FF" },
};
const textFieldDarkSx = {
  ...baseFieldDark,
  "& .MuiInputBase-input": { color: "#E6ECFF" },
};
const selectDarkSx = {
  ...baseFieldDark,
  "& .MuiSelect-icon": { color: "#E6ECFF" },
  "& .MuiSvgIcon-root": { color: "#E6ECFF" },
};
const menuPaperDarkSx = {
  bgcolor: "#0B173C",
  color: "#E6ECFF",
  border: "1px solid rgba(255,255,255,0.12)",
};
const checkboxDarkSx = {
  color: "rgba(230,236,255,0.7)",
  "&.Mui-checked": { color: "#90CAF9" },
};
/* ----------------------------------------------------------------------------------------- */
